'use client';

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import {
  canAccessDashboard,
  requiresOnboarding,
  resolvePostAuthRedirect,
} from '@/lib/auth-routing';
import { Sidebar } from '@/components/layout/sidebar';
import { TopBar } from '@/components/layout/topbar';
import { CommandPalette } from '@/components/layout/command-palette';

export function AppShell({ children }: { children: React.ReactNode }) {
  const { user, organizations, currentOrgId, setCurrentOrg, loading } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (loading) return;

    if (!user) {
      navigate('/login?redirect=/dashboard', { replace: true });
      return;
    }

    if (!user.emailVerified || user.status === 'pending_verification') {
      navigate('/verify-email/pending', { replace: true });
      return;
    }

    if (user.isStaff && organizations.length === 0) {
      navigate('/admin', { replace: true });
      return;
    }

    if (requiresOnboarding(user, organizations)) {
      navigate('/onboarding', { replace: true });
      return;
    }

    if (!canAccessDashboard(user, organizations)) {
      navigate(resolvePostAuthRedirect(user, organizations), { replace: true });
    }
  }, [loading, user, organizations, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-brand border-t-transparent" />
      </div>
    );
  }

  if (!user || !canAccessDashboard(user, organizations)) {
    return null;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      <Sidebar
        userEmail={user.email}
        userIsStaff={user.isStaff}
        organizations={organizations}
        currentOrgId={currentOrgId}
        onOrgChange={setCurrentOrg}
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onOpenNav={() => setMobileOpen(true)} onOpenCommand={() => setCommandOpen(true)} />
        <main className="flex-1 overflow-y-auto surface-noise">
          <div className="mx-auto w-full max-w-[1400px] px-3 py-5 sm:px-4 sm:py-6 md:px-8 md:py-8">
            {children}
          </div>
        </main>
      </div>

      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
    </div>
  );
}
